import React from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { Button, Flex } from "antd"
import { numberToCurrency } from "helpers/string"
import { Icon } from "ui/Icon"
import { getExperienceCoverImage } from "../helpers"
import { describeBookingDate, describeGuests, describeTickets, isTicketedSelection } from "../bookingDescription"
import CartAddOns from "./CartAddOns"
import styles from "./WidgetCart.module.css"

// One experience in the cart: photo, title, date and guests (or tickets), its price with the
// undiscounted one struck through, Remove, and its add-ons. When its experience could not be loaded
// (removed, unpublished) it says so and can only be removed. `holdIssue` says why its places are no
// longer held (getHoldIssue), with "Choose another time"; `isNotReserved` marks it no longer reserved
// after the time ran out.
const CartItem = ({ item, experience, summary, isUnavailable, holdIssue, isNotReserved, isAddOnsOpen, onRemove, onChooseAnotherTime, onAddOnsChange }) => {
  const { t } = useTranslation()
  const removeButton = (
    <Button type="link" className={styles.removeButton} onClick={onRemove}>
      {t("booking_widget.cart.remove")}
    </Button>
  )

  if (isUnavailable) {
    return (
      <Flex justify="space-between" align="center" gap={16} className={`${styles.item} ${styles.itemBody}`} role="alert">
        <Flex vertical gap={4} className={styles.itemText}>
          <span className={styles.itemTitle}>{t("booking_widget.unavailable.title")}</span>
          <span className={styles.itemMeta}>{t("booking_widget.unavailable.text")}</span>
        </Flex>
        {removeButton}
      </Flex>
    )
  }

  const coverImage = getExperienceCoverImage(experience)
  return (
    <Flex vertical gap={16}>
      <Flex className={styles.item}>
        <div className={styles.itemImage}>{coverImage && <img src={coverImage} alt={experience.title} />}</div>
        {/* Price and Remove on the right; on a phone, Remove and the price share a row under the text. */}
        <div className={styles.itemBody}>
          <Flex vertical gap={4} className={styles.itemText}>
            <span className={styles.itemTitle}>{experience.title}</span>
            <span className={styles.itemMeta}>{describeBookingDate({ experience, selection: item, t })}</span>
            <span className={styles.itemMeta}>
              {isTicketedSelection(item) ? describeTickets({ experience, ticketCounts: item.ticketCounts, t }) : describeGuests({ counts: item.counts, t })}
            </span>
          </Flex>
          <div className={styles.itemPrice}>
            {summary.originalExperienceTotal != null && <span className={styles.struck}>{numberToCurrency(summary.originalExperienceTotal)}</span>}
            <span className={styles.itemAmount}>{numberToCurrency(summary.experienceTotal)}</span>
          </div>
          <div className={styles.itemRemove}>{removeButton}</div>
        </div>
      </Flex>
      {isNotReserved && (
        <Flex gap={8} className={styles.holdIssue}>
          <Icon type="feather" name="Clock" width={20} height={20} color="currentColor" className={styles.holdIssueIcon} />
          <span>{t("booking_widget.hold.item_not_reserved")}</span>
        </Flex>
      )}
      {holdIssue && (
        <Flex gap={8} className={styles.holdIssue} role="alert">
          <Icon type="feather" name="AlertTriangle" width={20} height={20} color="currentColor" className={styles.holdIssueIcon} />
          <Flex vertical gap={4} align="flex-start">
            <span>
              <strong>{t("booking_widget.experience.availability_changed")}</strong>: {holdIssue}
            </span>
            <Button type="link" className={styles.removeButton} onClick={onChooseAnotherTime}>
              {t("booking_widget.checkout.choose_another_time")}
            </Button>
          </Flex>
        </Flex>
      )}
      <CartAddOns addOns={experience.add_ons} selections={item.addOns || {}} onChange={onAddOnsChange} isInitiallyOpen={isAddOnsOpen} />
    </Flex>
  )
}

CartItem.propTypes = {
  item: PropTypes.shape({
    cartId: PropTypes.string.isRequired,
    addOns: PropTypes.object,
    counts: PropTypes.object,
    ticketCounts: PropTypes.object
  }).isRequired,
  experience: PropTypes.object,
  summary: PropTypes.shape({
    experienceTotal: PropTypes.number,
    originalExperienceTotal: PropTypes.number
  }),
  isUnavailable: PropTypes.bool,
  holdIssue: PropTypes.string,
  isNotReserved: PropTypes.bool,
  isAddOnsOpen: PropTypes.bool,
  onRemove: PropTypes.func.isRequired,
  onChooseAnotherTime: PropTypes.func,
  onAddOnsChange: PropTypes.func.isRequired
}

CartItem.defaultProps = {
  experience: null,
  summary: null,
  isUnavailable: false,
  holdIssue: null,
  isNotReserved: false,
  onChooseAnotherTime: () => {},
  isAddOnsOpen: true
}

export default CartItem
